import config from "@Config/index";
import { assetUrl } from "../utils/assetUrl";
import { resolveUnikitoutGallery, type UnikitoutGalleryRow } from "../utils/resolvedGallery";

/** Payload CMS base URL — reuses the app-wide `CMS_API_URL`. */
const CMS_BASE_URL = config.CMS_API_URL;
const productsRequestCache = new Map<string, Promise<CmsProduct[]>>();

export interface CmsOptionValue {
  label: string;
  priceModifier?: number;
  description?: string;
  /** Tags that must match `tags` on product `images` rows (e.g. bedding-style:white) */
  imageTags?: string[] | null;
  /** Legacy / JSON import: direct URL strings */
  heroImageUrl?: string;
  galleryImageUrls?: string[];
  /** CMS: media upload for variant hero (Payload `depth` ≥ 2 populates `url`) */
  heroImage?: { url?: string } | number | null;
  /** CMS: ordered carousel rows `{ image: media }` */
  galleryImages?: Array<{ image?: { url?: string } | number | null; id?: string | null } | null>;
}

export interface CmsOption {
  name: string;
  values?: CmsOptionValue[];
}

export interface CmsBreakdown {
  title: string;
  description?: any;
}

export interface CmsSpecSection {
  title: string;
  items?: Array<{ item: string } | string>;
}

export interface CmsProduct {
  id: number;
  name: string;
  slug: string;
  shortDescription?: string;
  tags?: string[];
  highlights?: string[];
  basePrice: number;
  description?: any;
  category?: { id: number; name: string; slug: string } | number;
  images?: Array<{
    image?: { url?: string } | number;
    isFeatured?: boolean;
    /** When set, used with option `imageTags` to build the PDP carousel */
    tags?: string[] | null;
  }>;
  breakdowns?: CmsBreakdown[];
  careNotes?: Array<{ note: string } | string>;
  qualityInfo?: string;
  options?: CmsOption[];
  kitSpecifications?: {
    intro?: any;
    sections?: CmsSpecSection[];
  };
  /** Percentage discount off the pre-discount price. Null when unset. */
  discountPercentage?: number | null;
  /** "N+ students grabbed this" count. Null when unset. */
  studentsGrabbedCount?: number | null;
  /** Individual reviews; rating shown is the average of `reviewRating`. */
  reviews?: Array<{
    id?: string;
    reviewerName?: string | null;
    reviewRating?: number | null;
    reviewerUniversity?: string | null;
    reviewTitle?: string | null;
    reviewText?: string | null;
  }> | null;
  isAvailable?: boolean;
}

export interface UiExtraVariant {
  optionName: string;
  values: string[];
}

export interface UiExtraProduct {
  id: number;
  slug: string;
  name: string;
  price: number;
  image: string;
  images: string[];
  description: string;
  highlights: string[];
  variants?: UiExtraVariant[];
  compareAtPrice?: number;
  /** false when CMS marks the product unavailable */
  isAvailable?: boolean;
}

type FetchProductsOptions = {
  categorySlug?: string;
  slug?: string;
  limit?: number;
  useCache?: boolean;
  /** When true, CMS returns only products with isAvailable === true (e.g. cart "Don't Forget The Extras"). */
  onlyAvailable?: boolean;
  /**
   * Payload `sort` (e.g. `basePrice` ascending, `-basePrice` descending).
   * Defaults to price ascending for category/home listings. Use `null` to omit `sort` entirely.
   */
  sort?: string | null;
};

function buildProductsUrl(options: FetchProductsOptions = {}): string {
  const params = new URLSearchParams();
  params.set("limit", String(options.limit ?? 200));
  params.set("depth", "2");
  const isExtrasCategory = options.categorySlug === "extras";
  if (!isExtrasCategory && options.sort !== null) {
    params.set("sort", options.sort ?? "basePrice");
  }
  if (options.onlyAvailable) params.set("where[isAvailable][equals]", "true");
  if (options.categorySlug) params.set("where[category.slug][equals]", options.categorySlug);
  if (options.slug) params.set("where[slug][equals]", options.slug);
  return `${CMS_BASE_URL}/api/unikitout-products?${params.toString()}`;
}

export async function fetchUniKitProducts(
  options: FetchProductsOptions = {},
): Promise<CmsProduct[]> {
  const useCache = options.useCache !== false;
  const url = buildProductsUrl(options);
  if (useCache) {
    const cached = productsRequestCache.get(url);
    if (cached) return cached;
  }

  const request = fetch(url).then(async (res) => {
    if (!res.ok) throw new Error(`Failed to fetch UniKitOut products: ${res.status}`);
    const json = await res.json();
    return (json?.docs || []) as CmsProduct[];
  });

  if (useCache) productsRequestCache.set(url, request);
  return request;
}

function withGumletCacheBust(url: string): string {
  // Newly uploaded local-uploads keys can hit a cached Gumlet 404 until busted.
  if (!url.includes("webflow-amber-prod.gumlet.io/local-uploads/")) return url;
  const base = url.split("?")[0];
  return `${base}?v=rangelist`;
}

function mediaRowUrl(row: { image?: unknown }): string | undefined {
  const img = row.image;
  if (
    img &&
    typeof img === "object" &&
    "url" in img &&
    typeof (img as { url?: string }).url === "string"
  ) {
    return withGumletCacheBust((img as { url: string }).url);
  }
  return undefined;
}

/** Listing cards: images visible with no variant selected (`default` + untagged rows). */
export function getCmsProductImageUrls(product: CmsProduct): string[] {
  const rows = resolveUnikitoutGallery((product.images || []) as UnikitoutGalleryRow[], []);
  return rows.map(mediaRowUrl).filter((url): url is string => Boolean(url));
}

export function cmsProductToUiExtra(product: CmsProduct): UiExtraProduct {
  const images = getCmsProductImageUrls(product);
  const variants = (product.options || [])
    .filter((option) => option?.name && Array.isArray(option.values) && option.values.length > 0)
    .map((option) => ({
      optionName: option.name,
      values: (option.values || []).map((v) => v?.label).filter((v): v is string => Boolean(v)),
    }))
    .filter((option) => option.values.length > 0);

  return {
    id: product.id,
    slug: product.slug,
    name: product.name,
    price: Number(product.basePrice || 0),
    image: images[0] || getCmsImageUrl(product),
    images,
    description: product.shortDescription || lexicalToPlainText(product.description) || "",
    highlights:
      ((product.highlights || []).filter(Boolean) as string[]).length > 0
        ? ((product.highlights || []).filter(Boolean) as string[])
        : (product.breakdowns || []).map((b) => b.title).filter(Boolean),
    variants: variants.length > 0 ? variants : undefined,
    isAvailable: product.isAvailable !== false,
  };
}

export async function fetchUniKitExtras(): Promise<UiExtraProduct[]> {
  const products = await fetchUniKitProducts({ categorySlug: "extras", onlyAvailable: true });
  return products.map(cmsProductToUiExtra);
}

export async function fetchUniKitProductBySlug(slug: string): Promise<CmsProduct | null> {
  if (!slug) return null;
  const products = await fetchUniKitProducts({ slug, limit: 1, onlyAvailable: true });
  return products[0] || null;
}

export function lexicalToPlainText(value: any): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  const blocks = value?.root?.children || [];
  const paragraphs: string[] = [];
  blocks.forEach((block: any) => {
    const line = (block?.children || [])
      .map((child: any) => (typeof child?.text === "string" ? child.text : ""))
      .join("")
      .trim();
    if (line) paragraphs.push(line);
  });
  return paragraphs.join("\n\n");
}

export function getCmsImageUrl(product: CmsProduct): string {
  const featured = (product.images || []).find(
    (img) => img?.isFeatured && typeof img?.image === "object",
  );
  const fallback = (product.images || []).find((img) => typeof img?.image === "object");
  const url = (featured?.image as any)?.url || (fallback?.image as any)?.url;
  return url ? withGumletCacheBust(url) : assetUrl("/assets/images/unikit-card.png");
}
